import { styled } from '@mui/material/styles';
import { CloseFullscreenRounded, OpenInFullRounded } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import Dot from 'components/common/dot/Dot';
import UserAvatar from 'components/common/userAvatar/UserAvatar';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import ArticleLineIcon from 'remixicon-react/ArticleLineIcon';
import { ArticleSourceType, ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { ReactComponent as SparkIcon } from 'assets/icons/auto-generated.svg';
import useUserData from 'hooks/useUserData';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTemplatesQuery } from '@buildwise/libs/company-admin';
import QuestionFillIcon from 'remixicon-react/QuestionFillIcon';
import { theme } from 'config/theme';
import ChatQuoteFillIcon from 'remixicon-react/ChatQuoteFillIcon';
import useCompanyQueryData from 'hooks/useCompanyQueryData';
import { useBuildWiseModules } from '@buildwise/libs/helpers';

type PostFormDialogTitleProps = {
  article: ArticleType | undefined;
  articleType: ArticleSourceType;
  createPlaylist: boolean;
  forward: boolean;
  isEditing: boolean;
  isFullScreen: boolean;
  handleClose: () => void;
  setFullScreen: Dispatch<SetStateAction<boolean>>;
  setOpenTemplates: Dispatch<SetStateAction<boolean>>;
  setOpenWriteWithAIModal: Dispatch<SetStateAction<boolean>>;
};

function PostFormDialogTitle({
  article,
  articleType,
  createPlaylist,
  forward,
  isEditing,
  isFullScreen,
  handleClose,
  setFullScreen,
  setOpenTemplates,
  setOpenWriteWithAIModal,
}: PostFormDialogTitleProps) {
  const { shouldDisplayCompanyOnUserCards } = useCompanyQueryData();
  const { user, companyId, company } = useUserData();
  const hasAccessToBrowseTemplates = useMemo(
    () => !createPlaylist && company?.settings?.articleTemplatesFlag,
    [createPlaylist, company?.settings?.articleTemplatesFlag],
  );
  const { templates } = useTemplatesQuery(companyId, hasAccessToBrowseTemplates);
  const articleUserInfo = useMemo(
    () =>
      isEditing && !article?.isDraft
        ? {
            userNameToDisplay: article?.userNameToDisplay || '',
            userNameTitleToDisplay: article?.userNameTitleToDisplay || '',
            primaryCompanyName: article?.primaryCompanyName || '',
            userPrimaryCompanyName: article?.userPrimaryCompanyName || '',
            createdBy: article?.createdBy || '',
            profileImageURL: article?.profileImageURL || '',
          }
        : {
            userNameToDisplay: user?.userNameToDisplay || '',
            userNameTitleToDisplay: user?.title || '',
            primaryCompanyName: user?.primaryCompanyName || '',
            userPrimaryCompanyName: user?.userPrimaryCompanyName || '',
            createdBy: user?.username || '',
            profileImageURL: user?.profileImageURL || '',
          },
    [article?.isDraft, user],
  );
  const { homeModuleFeatures } = useBuildWiseModules();

  return (
    <CustomDialogTitle
      onClose={handleClose}
      id="dialog-title"
      maxWidth={theme.maxWidth}
      margin="0 auto"
      width="100%"
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mr={5}>
        <Box display="flex" alignItems="center">
          {forward ? (
            <Title>Forward to Workspace</Title>
          ) : (
            <>
              {articleType === ArticleSourceType.POST ? (
                <UserAvatar
                  title={articleUserInfo?.userNameToDisplay?.trim() || ''}
                  avatarName={articleUserInfo?.userNameToDisplay?.trim() || ''}
                  subTitle={
                    <>
                      {articleUserInfo?.userNameTitleToDisplay}
                      {shouldDisplayCompanyOnUserCards &&
                        (articleUserInfo?.primaryCompanyName ||
                          articleUserInfo?.userPrimaryCompanyName) && (
                          <>
                            <Dot />
                            {articleUserInfo?.primaryCompanyName ||
                              articleUserInfo?.userPrimaryCompanyName}
                          </>
                        )}
                    </>
                  }
                  avatarSize={40}
                  userName={articleUserInfo?.createdBy}
                  target="_blank"
                  profileImageURL={articleUserInfo?.profileImageURL}
                />
              ) : articleType === ArticleSourceType.QUESTION ? (
                <Box display="flex" alignItems="center">
                  <QuestionFillIcon
                    color={theme.palette.custom.dodgerBlue}
                    size={26}
                    style={{ marginRight: 8 }}
                  />
                  <Title>{homeModuleFeatures['Ask Peers'].displayName}</Title>
                </Box>
              ) : (
                <Box display="flex" alignItems="center">
                  <ChatQuoteFillIcon
                    color={theme.palette.custom.cloverGreen}
                    size={26}
                    style={{ marginRight: 8 }}
                  />
                  <Title>{homeModuleFeatures['Suggest Improvements'].displayName}</Title>
                </Box>
              )}
            </>
          )}
        </Box>
        <Box display="flex" alignItems="center">
          {articleType === ArticleSourceType.POST && (
            <>
              {hasAccessToBrowseTemplates && templates.length > 0 && (
                <Button
                  size={'medium'}
                  variant="text"
                  startIcon={<ArticleLineIcon />}
                  onClick={() => setOpenTemplates(true)}
                  sx={(theme) => ({
                    mr: 1.5,
                    borderRadius: '4px',
                    color: theme.palette.custom.tuna,
                    backgroundColor: theme.palette.custom.background,
                  })}
                >
                  Select Templates
                </Button>
              )}
              {!article?.isDraft && article?.status !== 'PUBLISHED' && (
                <Button
                  sx={(theme) => ({
                    borderRadius: 1,
                    backgroundColor: theme.palette.custom.aliceBlue,
                    color: theme.palette.custom.link,
                    marginRight: 2,
                  })}
                  startIcon={<SparkIcon width={24} height={24} />}
                  onClick={() => {
                    setOpenWriteWithAIModal(true);
                  }}
                >
                  Write with AI
                </Button>
              )}
            </>
          )}

          <IconButton onClick={() => setFullScreen((prevState) => !prevState)}>
            {isFullScreen ? (
              <CloseFullscreenRounded sx={(theme) => ({ color: theme.palette.custom.black })} />
            ) : (
              <OpenInFullRounded sx={(theme) => ({ color: theme.palette.custom.black })} />
            )}
          </IconButton>
        </Box>
      </Box>
    </CustomDialogTitle>
  );
}

const Title = styled(Typography, { label: 'StickyContainer' })(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.custom.black,
}));

export default PostFormDialogTitle;
